
import React, { useEffect } from 'react';
import PendingVerificationCard from './PendingVerificationCard';
import RejectedAccountCard from './RejectedAccountCard';
import NinjaOnePortalCard from './NinjaOnePortalCard';
import AdminDashboardCard from './AdminDashboardCard';
import EmployeeResourcesCard from './EmployeeResourcesCard';
import WaveClientDashboard from './WaveClientDashboard';
import ClientPortalWelcome from './ClientPortalWelcome';

interface DashboardContentProps {
  userStatus: string | null;
  isAdmin: boolean;
  userData?: any;
}

const DashboardContent: React.FC<DashboardContentProps> = ({ 
  userStatus, 
  isAdmin,
  userData 
}) => {
  console.log('Current user status in DashboardContent:', userStatus);
  
  // Log status changes for debugging
  useEffect(() => {
    console.log('Dashboard content status updated:', userStatus);
  }, [userStatus]);
  
  // Function to determine which status card to show
  const renderStatusCard = () => {
    // Only show status cards for non-verified states
    if (userStatus === 'pending' || userStatus === 'pending_verification') {
      return <PendingVerificationCard />;
    }
    
    if (userStatus === 'rejected') {
      return <RejectedAccountCard />;
    }
    
    // Don't show any status card for approved/verified users
    return null;
  };
  
  // Function to determine if user should have access to client features
  const hasClientAccess = () => {
    return userStatus === 'approved' || userStatus === 'verified';
  };

  // Check if user has billing data (previously Wave data)
  const hasBillingData = userData?.wave_customer_id;
  const isNewUser = userData?.account_setup_completed_at && 
    new Date(userData.account_setup_completed_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Within last 7 days
  
  return (
    <div className="space-y-6">
      {/* Status Card - Only shows for pending or rejected status */}
      {renderStatusCard()}
      
      {/* Welcome Card for verified/approved users */}
      {hasClientAccess() && (
        <ClientPortalWelcome 
          clientName={userData?.full_name || 'Client'}
          accountSetupCompleted={!!userData?.account_setup_completed_at}
          hasBillingData={hasBillingData}
          isNewUser={isNewUser}
        />
      )}
      
      {/* Billing Client Dashboard - only for verified/approved users with billing data */}
      {hasClientAccess() && hasBillingData && (
        <div data-tour="billing-data">
          <WaveClientDashboard 
            clientId={userData?.id}
            waveCustomerId={userData?.wave_customer_id}
          />
        </div>
      )}
      
      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* NinjaOne Portal Card - only for verified/approved users */}
        {hasClientAccess() && (
          <div data-tour="ninjaone-portal">
            <NinjaOnePortalCard />
          </div>
        )}
        
        {/* Admin access */}
        {isAdmin && (
          <>
            <AdminDashboardCard />
            <EmployeeResourcesCard />
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardContent;
